import { match, P } from "ts-pattern";
import { formatDateTimeFromISO } from "widgets";

import { CellType } from "../../api/openapi.ts";
import { getPercentDisplayValue } from "../utils/percent.ts";

export const getValueByCellType = (cellType: CellType, value: unknown): string => {
  if (value == undefined) {
    return "";
  }

  return (
    match(cellType)
      .with(
        P.union(
          // Numeric Types
          CellType.LONG,
          CellType.INTEGER,
          CellType.SCIENTIFIC,
          CellType.FLOAT,
          CellType.DOUBLE,
          CellType.BIG_DECIMAL,
          CellType.LONG_LIST,
          CellType.INTEGER_LIST,
          CellType.FLOAT_LIST,
          CellType.DOUBLE_LIST,
          CellType.NESTED_INTEGER_LIST,
          CellType.NESTED_LONG_LIST,
          CellType.NESTED_FLOAT_LIST,
          CellType.NESTED_DOUBLE_LIST,

          //  String types
          CellType.STRING,
          CellType.STRING_LIST,
          CellType.NESTED_STRING_LIST,
          CellType.STRAND,
          CellType.STRAND_LIST,

          // FIXME: Cells with missing renderers
          CellType.FILE,
          CellType.FILE_LIST,
          CellType.ORGANISM,
          CellType.VARIANT_PROFILE,
        ),
        () => {
          const v = Array.isArray(value) ? value : [value];
          return v
            .flatMap((val) =>
              typeof val === "number"
                ? val === 0 || Math.abs(val) > 10e-3
                  ? val.toLocaleString("en-US", { useGrouping: false })
                  : (val?.toExponential() ?? "")
                : (val ?? ""),
            )
            .join(", ");
        },
      )
      // Date cells
      .with(P.union(CellType.DATE, CellType.DATE_LIST), () => {
        const v = Array.isArray(value) ? value : [value];
        return v.map((val) => formatDateTimeFromISO(val, { hideTime: true }) ?? "").join(", ");
      })
      // DateTime cells
      .with(CellType.DATE_TIME, () =>
        typeof value === "string" ? (formatDateTimeFromISO(value, { hideTime: false }) ?? "") : "",
      )
      // Color cell - extract value
      .with(
        P.union(CellType.COLOR_NUM, CellType.COLOR_NUM_LIST, CellType.COLOR_STRING, CellType.COLOR_STRING_LIST),
        () => {
          const v = Array.isArray(value) ? value : [value];
          return v.flatMap((v) => v?.value ?? "").join(", ");
        },
      )
      // Percent cell - account for NaN
      .with(CellType.PERCENT, () => (typeof value === "string" ? (getPercentDisplayValue(value) ?? "") : ""))
      // Boolean cell
      .with(P.union(CellType.BOOLEAN, CellType.BOOLEAN_LIST), () => {
        const v = Array.isArray(value) ? value : [value];
        // boolean cell can be undefined, true or false. We keep the undefined for now.
        // In Table cells, undefined is displayed as a non-breaking-space
        return v.map((val) => (val == null ? "" : String(val))).join(", ");
      })
      // Link cells - map and join labels
      .with(P.union(CellType.LINK, CellType.LINK_LIST), () => {
        const v = Array.isArray(value) ? value : [value];
        return v
          .map((v: Record<string, unknown>) => {
            const value = v?.["label"];
            return typeof value === "string" ? value : "";
          })
          .join(", ");
      })
      // External link lists - map and join
      .with(P.union(CellType.LINK_EXTERNAL, CellType.LINK_EXTERNAL_LIST), () => {
        const v = Array.isArray(value) ? value : [value];
        return v
          .map((v: Record<string, unknown>) => {
            const value = v?.["link"];
            return typeof value === "string" ? value : "";
          })
          .join(", ");
      })
      // Sample cells
      .with(P.union(CellType.SAMPLE, CellType.SAMPLE_LIST), () => {
        const v = Array.isArray(value) ? value : [value];
        return v
          .map((v: Record<string, unknown>) => {
            const value = v?.["name"];
            return typeof value === "string" ? value : "";
          })
          .join(", ");
      })
      // BLAST cell - not implemented yet
      .with(CellType.BLAST_ALIGNMENT_LIST, () => "[Not Supported]")
      .exhaustive()
  );
};
